/**
 * Superuser Context
 * Gerencia acesso de superusuários aos painéis administrativos
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { checkIsSuperuser } from './adminService';
import { logger } from '../logger';

const SuperuserContext = createContext(null);

export const SuperuserProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifySuperuser = async () => {
      if (authLoading) return;

      if (!user) {
        setIsSuperuser(false);
        setLoading(false);
        return;
      }

      try {
        const result = await checkIsSuperuser(user.id);
        setIsSuperuser(result);
        logger.log(`🔐 Superuser check: ${result ? 'Autorizado' : 'Não autorizado'}`);
      } catch (err) {
        logger.error('Erro ao verificar superusuário:', err);
        setError(err.message);
        setIsSuperuser(false);
      } finally {
        setLoading(false);
      }
    };

    verifySuperuser();
  }, [user, authLoading]);

  const value = {
    isSuperuser,
    loading: loading || authLoading,
    error,
  };

  return (
    <SuperuserContext.Provider value={value}>
      {children}
    </SuperuserContext.Provider>
  );
};

export const useSuperuser = () => {
  const context = useContext(SuperuserContext);
  if (!context) {
    throw new Error('useSuperuser deve ser usado dentro de SuperuserProvider');
  }
  return context;
};

/**
 * HOC para proteger rotas de superusuário
 */
export const SuperuserRoute = ({ children }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isSuperuser, loading: superuserLoading } = useSuperuser();

  useEffect(() => {
    if (!authLoading && !user) {
      logger.warn('⛔ Acesso negado: Usuário não autenticado');
      navigate('/user-login', { replace: true });
    } else if (!authLoading && !superuserLoading && !isSuperuser) {
      logger.warn('⛔ Acesso negado: Usuário não é superusuário');
      navigate('/user-dashboard', { replace: true });
    }
  }, [authLoading, superuserLoading, user, isSuperuser, navigate]);

  if (authLoading || superuserLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground font-body">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (!user || !isSuperuser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md p-8">
          <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-heading font-heading-bold text-foreground">
            Acesso Restrito
          </h1>
          <p className="text-muted-foreground">
            Você não tem permissão para acessar esta área. Esta seção é restrita a administradores do sistema.
          </p>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default SuperuserContext;
